/* eslint-disable */
import { Clock3, MessageSquare, PencilLine, Search, Trash2 } from 'lucide-react';
import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { deleteChatSession, renameChatSession } from '../../store/ai/aiChatSlice';

const hasConversationMessages = (session) =>
  Array.isArray(session?.messages) && session.messages.some((message) => message.role === 'user');

const getSessionDisplayTitle = (session, activeSessionId) => {
  if (session?.title) {
    return session.title;
  }

  return session?.sessionId === activeSessionId ? 'New chat' : 'Untitled conversation';
};

const formatDateTime = (timestamp) => {
  if (!timestamp) {
    return '';
  }

  try {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(new Date(timestamp));
  } catch (error) {
    return '';
  }
};

const ChatSessionHistoryPanel = (props) => {
  const {
    sessions = [],
    activeSessionId,
    theme = 'dark',
    onSelectSession,
    onStartNewSession,
  } = props;
  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState('');

  const visibleSessions = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return sessions
      .filter((session) => hasConversationMessages(session) || session.sessionId === activeSessionId || session.loading)
      .filter((session) => {
        if (!normalizedSearch) {
          return true;
        }

        return `${getSessionDisplayTitle(session, activeSessionId)} ${formatDateTime(session.createdAt)} ${formatDateTime(session.updatedAt)}`
          .toLowerCase()
          .includes(normalizedSearch);
      })
      .sort((first, second) => new Date(second.updatedAt || 0) - new Date(first.updatedAt || 0));
  }, [activeSessionId, sessions, searchTerm]);

  const emptyLabel = sessions.some(hasConversationMessages)
    ? 'No matching sessions found.'
    : 'No previous chat sessions yet.';

  const handleRenameSession = (session) => {
    const nextTitle = window.prompt('Rename chat session', session.title || 'Untitled conversation');
    if (!nextTitle || !nextTitle.trim() || nextTitle.trim() === session.title) {
      return;
    }

    dispatch(
      renameChatSession({
        sessionId: session.sessionId,
        title: nextTitle.trim(),
      }),
    );
  };

  const handleDeleteSession = (session) => {
    const confirmed = window.confirm(`Delete "${session.title || 'Untitled conversation'}"?`);
    if (!confirmed) {
      return;
    }

    dispatch(deleteChatSession(session.sessionId));
  };

  return (
    <aside
      className={`flex h-full w-[280px] max-w-[calc(100vw-32px)] flex-col border-r ${
        theme === 'dark'
          ? 'border-[#25384F] bg-[#182433] text-[#C8C8C8] shadow-[8px_0_24px_rgba(0,0,0,0.35)]'
          : 'border-[#E5E5E5] bg-[#FFFFFF] text-[#202020] shadow-[8px_0_24px_rgba(0,0,0,0.08)]'
      }`}
    >
      <div className={`border-b px-4 py-3 ${theme === 'dark' ? 'border-[#25384F]' : 'border-[#E5E5E5]'}`}>
        <div className="min-w-0">
          <p className={`text-xs font-semibold ${theme === 'dark' ? 'text-[#48A7FF]' : 'text-[#066FD1]'}`}>
            History
          </p>
          <h3 className={`mt-0.5 truncate text-sm font-semibold ${theme === 'dark' ? 'text-white' : 'text-[#202020]'}`}>
            Chat sessions
          </h3>
        </div>

        <div
          className={`mt-3 flex items-center gap-2 rounded-md border px-3 py-2 ${
            theme === 'dark' ? 'border-[#25384F] bg-[#1B2D42]' : 'border-[#D1E2F0] bg-[#F8FBFE]'
          }`}
        >
          <Search className={`h-4 w-4 flex-shrink-0 ${theme === 'dark' ? 'text-[#A3B1C9]' : 'text-[#6C7A91]'}`} />
          <input
            type="search"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Search sessions"
            className={`min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:opacity-100 ${
              theme === 'dark' ? 'text-white placeholder:text-[#A3B1C9]' : 'text-[#0A2342] placeholder:text-[#6C7A91]'
            }`}
          />
        </div>
      </div>

      <div
        className="min-h-0 flex-1 overflow-y-auto p-3 overscroll-contain"
        style={{ overscrollBehavior: 'contain' }}
      >
        {visibleSessions.length ? (
          <div className="space-y-2">
            {visibleSessions.map((session) => {
              const isActive = session.sessionId === activeSessionId;

              return (
                <div
                  key={session.sessionId}
                  role="button"
                  tabIndex={0}
                  onClick={() => onSelectSession?.(session.sessionId)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      onSelectSession?.(session.sessionId);
                    }
                  }}
                  className={`w-full cursor-pointer rounded-[10px] border px-3 py-3 transition ${
                    isActive
                      ? theme === 'dark'
                        ? 'border-[#066FD1] bg-[#25384F] shadow-sm'
                        : 'border-[#066FD1] bg-[#EFF8FE]'
                      : theme === 'dark'
                        ? 'border-[#25384F] bg-[#1B2D42] hover:border-[#48A7FF]/50 hover:bg-[#25384F]'
                        : 'border-[#E5E5E5] bg-white hover:border-[#066FD1]/40 hover:bg-[#F8FBFE]'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <span
                      className={`mt-0.5 inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full ${
                        isActive
                          ? 'bg-[#48A7FF] text-[#0A2342]'
                          : theme === 'dark'
                            ? 'bg-[#08111B] text-[#A3B1C9]'
                            : 'bg-[#F8FBFE] text-[#6C7A91]'
                      }`}
                    >
                      <MessageSquare className="h-4 w-4" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className={`block truncate text-sm font-semibold ${theme === 'dark' ? 'text-white' : 'text-[#0A2342]'}`}>
                        {getSessionDisplayTitle(session, activeSessionId)}
                      </span>
                      <span className={`mt-1 block text-xs ${theme === 'dark' ? 'text-[#A3B1C9]' : 'text-[#6C7A91]'}`}>
                        Created {formatDateTime(session.createdAt)}
                      </span>
                      {session.loading ? (
                        <span
                          className={`mt-1 flex items-center gap-1.5 text-xs font-medium ${
                            theme === 'dark' ? 'text-[#48A7FF]' : 'text-[#066FD1]'
                          }`}
                        >
                          <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-current" />
                          Generating response...
                        </span>
                      ) : (
                        <span
                          className={`mt-1 flex items-center gap-1 text-xs ${
                            theme === 'dark' ? 'text-[#A3B1C9]' : 'text-[#6C7A91]'
                          }`}
                        >
                          <Clock3 className="h-3.5 w-3.5" />
                          {formatDateTime(session.updatedAt)}
                        </span>
                      )}
                    </span>
                    <div className="ml-2 flex shrink-0 items-center gap-1">
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleRenameSession(session);
                        }}
                        className={`inline-flex h-8 w-8 items-center justify-center rounded-full transition ${
                          theme === 'dark'
                            ? 'text-[#A3B1C9] hover:bg-[#0D1621] hover:text-white'
                            : 'text-[#6C7A91] hover:bg-[#EFF8FE] hover:text-[#0A2342]'
                        }`}
                        aria-label={`Rename ${session.title || 'chat session'}`}
                      >
                        <PencilLine className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleDeleteSession(session);
                        }}
                        className={`inline-flex h-8 w-8 items-center justify-center rounded-full transition ${
                          theme === 'dark'
                            ? 'text-[#A3B1C9] hover:bg-[#3B1E2A] hover:text-[#FFB0AA]'
                            : 'text-[#6C7A91] hover:bg-[#FDECEC] hover:text-[#C62828]'
                        }`}
                        aria-label={`Delete ${session.title || 'chat session'}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div
            className={`flex h-full min-h-[220px] flex-col items-center justify-center rounded-[10px] border px-4 text-center ${
              theme === 'dark' ? 'border-[#25384F] bg-[#1B2D42] text-[#A3B1C9]' : 'border-[#E5E5E5] bg-[#F8FBFE] text-[#6C7A91]'
            }`}
          >
            <MessageSquare className="h-8 w-8 text-[#6FA8FF]" />
            <p className="mt-3 text-sm font-semibold">{emptyLabel}</p>
          </div>
        )}
      </div>
    </aside>
  );
};

ChatSessionHistoryPanel.propTypes = {
  sessions: PropTypes.arrayOf(
    PropTypes.shape({
      sessionId: PropTypes.string,
      title: PropTypes.string,
      createdAt: PropTypes.string,
      updatedAt: PropTypes.string,
      messages: PropTypes.array,
    }),
  ),
  activeSessionId: PropTypes.string,
  theme: PropTypes.oneOf(['light', 'dark']),
  onSelectSession: PropTypes.func,
  onStartNewSession: PropTypes.func,
};

export default ChatSessionHistoryPanel;
